import { Stack, Typography } from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { RatingCounter } from '../../../../../../components/Rating/RatingCounter'
import { FramedAvatar } from '../../../../../../sharedComponents/src/globalHeader/components/FramedAvatar'
import { useScreenSize } from '../../../../../../sharedComponents/src/hooks/useScreenSize'
import theme from '../../../../../../sharedComponents/src/Theme/theme-v2'
import { useStoreState } from '../../../../../../store/hooks'
import { Bot, PlayerInGameData, TimeMode } from '../../../../../../store/types'
import { GameOverColumnContent } from '../GameOverColumnContent'
import { RatingChange } from '../RatingChange/RatingChange'

const PlayerAvatar: React.FC<{ playerInfo: PlayerInGameData; customBorder?: string }> = (props) => {
    const { playerInfo, customBorder } = props
    const { avatarUrl, playerKind, reward, playerId } = playerInfo

    const bots = useStoreState((state) => state.matchMaker.bots)
    const [bot, setBot] = useState<Bot>()

    useEffect(() => {
        if (playerKind === 'machine') {
            const bot = bots.find((bot) => bot.id === playerId)
            if (bot) {
                setBot(bot)
            }
        }
    }, [])

    return (
        <Stack sx={{ position: bot?.isBotOfTheDay ? 'relative' : 'unset', alignItems: 'center' }}>
            <FramedAvatar
                avatar={avatarUrl}
                season={reward?.season}
                reward={reward?.tier}
                customBorder={customBorder}
                sx={{
                    maxWidth: { xs: '80px', md: '124px' },
                    width: '100%',
                    height: 'auto',
                    aspectRatio: '1',
                }}
            />
            {bot?.isBotOfTheDay && (
                <Typography
                    variant="h5"
                    sx={(theme) => ({
                        zIndex: 1,
                        fontSize: { xs: '10px !important', md: '16px !important' },
                        lineHeight: { xs: '16px', md: '26px' },
                        position: 'absolute',
                        textTransform: 'uppercase',
                        bottom: '0',
                        maxWidth: '124px',
                        width: '100%',
                        textAlign: 'center',
                        backgroundColor: 'primary.main',
                        borderRadius: '6px',
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        padding: '2px 4px',
                        color: theme.palette.getContrastText(theme.palette.primary.main),
                    })}
                >
                    featured bot
                </Typography>
            )}
        </Stack>
    )
}

type Props = {
    playerInfo: PlayerInGameData
    winner: boolean
    rated: boolean
    oldRating: number
    newRating: number
    timeMode: TimeMode
    emote?: ReactNode
}

export const PlayerColumn = ({ playerInfo, winner, rated, oldRating, newRating, timeMode, emote }: Props) => {
    const { userName, ratings } = playerInfo
    const settings = useStoreState((state) => state.gameView.settings)
    const { isMobile } = useScreenSize()

    return (
        <Stack spacing={{ xs: 1.5, md: 3 }} alignItems="center">
            <GameOverColumnContent winner={winner}>
                <PlayerAvatar
                    playerInfo={playerInfo}
                    customBorder={winner ? `2px solid ${theme.palette.secondary.main}` : undefined}
                />
                {emote}
            </GameOverColumnContent>
            <Stack spacing={'4px'} alignItems="center">
                <Typography
                    variant="body2"
                    sx={{
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: { xs: '14px', md: '18px' },
                    }}
                >
                    {userName}
                </Typography>
                {!settings.friendlyMode &&
                    (rated ? (
                        <Stack spacing={1} direction="row" alignItems="center">
                            <RatingCounter
                                oldRating={oldRating}
                                newRating={newRating}
                                type={timeMode.name}
                                isProvisional={ratings?.[timeMode.name]?.is_provisional}
                                textSize={isMobile ? 'body2' : 'body1'}
                            />
                            <RatingChange change={newRating > 0 ? newRating - oldRating : 0} />
                        </Stack>
                    ) : (
                        <Typography color="secondary" variant="body2" fontSize={isMobile ? '14px' : '18px'}>
                            Unrated
                        </Typography>
                    ))}
            </Stack>
        </Stack>
    )
}
